import { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
    CreditCard, Plane, Users, BarChart3, CheckCircle, Clock,
    XCircle, AlertCircle, ArrowUpRight, Shield, Loader2, Zap,
    RefreshCw, XSquare, ArrowUpCircle, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { AccountStatementCard } from '../components/billing/AccountStatementCard';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const planNames = { free: 'Free Trial', basic: 'Starter', pro: 'Professional', enterprise: 'Enterprise' };
const planColors = {
    free: 'bg-zinc-500/10 text-zinc-400 border-zinc-500/20',
    basic: 'bg-sky-500/10 text-sky-400 border-sky-500/20',
    pro: 'bg-amber-500/10 text-amber-400 border-amber-500/20',
    enterprise: 'bg-violet-500/10 text-violet-400 border-violet-500/20',
};

const statusConfig = {
    paid: { icon: CheckCircle, color: 'text-emerald-500', bg: 'bg-emerald-500/10', label: 'Pagado' },
    pending: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'Pendiente' },
    initiated: { icon: Clock, color: 'text-amber-500', bg: 'bg-amber-500/10', label: 'Iniciado' },
    unpaid: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-500/10', label: 'No pagado' },
    expired: { icon: AlertCircle, color: 'text-zinc-400', bg: 'bg-zinc-500/10', label: 'Expirado' },
};

const usd = (n) => { const v = Number(n || 0); return `USD ${v.toLocaleString('en-US', { minimumFractionDigits: v % 1 ? 2 : 0, maximumFractionDigits: 2 })}`; };
const fmtDate = (d) => new Date(`${String(d).slice(0, 10)}T12:00:00`).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });

function MonthlyBreakdown({ b }) {
    if (!b) return null;
    const line = (label, value, testid, strong) => (
        <div className={`flex justify-between text-sm ${strong ? 'font-bold border-t border-border/50 pt-2 mt-1 text-base' : ''}`}>
            <span className={strong ? '' : 'text-muted-foreground'}>{label}</span>
            <span className="font-mono" data-testid={testid}>{value}</span>
        </div>
    );
    return (
        <div className="mt-5 p-4 rounded-lg bg-muted/30 space-y-1.5" data-testid="monthly-breakdown">
            {line(`${b.aircraft_count} aeronaves facturables × ${usd(b.price_per_aircraft)}`, usd(b.aircraft_subtotal), 'breakdown-aircraft')}
            <p className="text-[11px] text-muted-foreground" data-testid="billable-note">Incluye todas las aeronaves salvo las almacenadas.</p>
            {b.modules.map(m => <div key={m.key}>{line(`Módulo ${m.label}`, usd(m.price_monthly_usd), `breakdown-module-${m.key}`)}</div>)}
            {b.founder_discount_pct > 0 && b.list_total != null && line('Subtotal', usd(b.list_total), 'breakdown-subtotal')}
            {b.founder_discount_pct > 0 && line(`Precio fundador −${b.founder_discount_pct}%${b.founder_discount_until ? ` hasta ${fmtDate(b.founder_discount_until)}` : ''}`, `− ${usd(b.founder_discount_amount)}`, 'breakdown-founder')}
            {b.pilot_active && b.total_monthly != null && line('Total mensual', usd(b.total_monthly), 'breakdown-total', true)}
            {b.pilot_active
                ? line('A pagar este mes', usd(0), 'breakdown-amount-due', b.total_monthly == null)
                : line('Total a pagar', usd(b.total_monthly), 'breakdown-total', true)}
            {!b.pilot_active && <p className="text-[11px] text-muted-foreground">Pago anual: {usd(b.total_yearly)} (2 meses sin costo).</p>}
            {b.pilot_active && (
                <p className="text-sm text-sky-400 flex items-center gap-2 pt-1" data-testid="pilot-notice">
                    <Clock className="w-4 h-4" /> Periodo de prueba hasta {fmtDate(b.pilot_until)}
                </p>
            )}
            {b.out_of_range && (
                <p className="text-xs text-amber-500 flex items-center gap-2 pt-1" data-testid="plan-range-notice">
                    <AlertCircle className="w-4 h-4 flex-shrink-0" /> Tu flota quedó fuera del rango de tu plan. Contacta a soporte para cambiar al plan {planNames[b.suggested_plan]}.
                </p>
            )}
        </div>
    );
}

function UsageBar({ label, icon: Icon, current, max }) {
    const unlimited = max === null || max === undefined;
    const pct = !unlimited && max > 0 ? Math.min((current / max) * 100, 100) : 0;
    const isWarning = pct >= 80;
    const isDanger = pct >= 95;
    return (
        <div className="space-y-2" data-testid={`usage-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm">
                    <Icon className="w-4 h-4 text-muted-foreground" />
                    <span className="font-medium">{label}</span>
                </div>
                <span className="text-sm font-mono font-bold">
                    <span className={isDanger ? 'text-red-500' : isWarning ? 'text-amber-500' : 'text-primary'}>{current}</span>
                    <span className="text-muted-foreground"> / {unlimited ? 'sin límite' : max}</span>
                </span>
            </div>
            <div className="h-2 rounded-full bg-muted overflow-hidden">
                <div
                    className={`h-full rounded-full transition-all duration-500 ${isDanger ? 'bg-red-500' : isWarning ? 'bg-amber-500' : 'bg-primary'}`}
                    style={{ width: `${pct}%` }}
                />
            </div>
        </div>
    );
}

export default function BillingPage() {
    const { user } = useAuth();
    const [data, setData] = useState(null);
    const [sub, setSub] = useState(null);
    const [loading, setLoading] = useState(true);
    const [confirmCancel, setConfirmCancel] = useState(false);
    const [confirmResume, setConfirmResume] = useState(false);
    const [upgradeOpen, setUpgradeOpen] = useState(false);
    const [upgradePlan, setUpgradePlan] = useState('pro');
    const [upgradeCycle, setUpgradeCycle] = useState('monthly');
    const [actionLoading, setActionLoading] = useState(false);

    const fetchAll = async () => {
        try {
            const [planRes, subRes] = await Promise.all([
                axios.get(`${API}/billing/my-plan`),
                axios.get(`${API}/billing/my-subscription`).catch(() => ({ data: null })),
            ]);
            setData(planRes.data);
            setSub(subRes.data);
        } catch (err) {
            toast.error('Error al cargar información de facturacion');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { fetchAll(); }, []);

    const doCancel = async () => {
        if (!sub?.stripe_subscription_id) return;
        setActionLoading(true);
        try {
            await axios.post(`${API}/billing/subscription/cancel`);
            toast.success('Cancelacion programada al final del período');
            setConfirmCancel(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al cancelar');
        } finally { setActionLoading(false); }
    };

    const doResume = async () => {
        if (!sub?.stripe_subscription_id) return;
        setActionLoading(true);
        try {
            await axios.post(`${API}/billing/subscription/resume`);
            toast.success('Suscripcion reactivada');
            setConfirmResume(false);
            fetchAll();
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Error al reactivar');
        } finally { setActionLoading(false); }
    };

    const doUpgrade = async () => {
        setActionLoading(true);
        try {
            const origin = window.location.origin;
            const r = await axios.post(`${API}/billing/subscription/checkout`, {
                plan: upgradePlan, billing_cycle: upgradeCycle, origin_url: origin,
            });
            if (r.data?.url) {
                window.location.href = r.data.url;
            } else {
                toast.error('Stripe no devolvio URL de checkout');
            }
        } catch (e) {
            const msg = e.response?.data?.detail || 'Error al crear checkout';
            toast.error(msg);
        } finally { setActionLoading(false); }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
        );
    }

    if (!data) {
        return (
            <div className="text-center py-16 text-muted-foreground">
                <CreditCard className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No se pudo cargar la informacion de facturacion.</p>
            </div>
        );
    }

    const { tenant, plan_details, usage, transactions } = data;
    const planKey = tenant.plan || 'free';

    return (
        <div className="space-y-6 max-w-5xl" data-testid="billing-page">
            {/* Header */}
            <div>
                <h1 className="text-2xl sm:text-3xl font-bold tracking-tight uppercase font-['Barlow_Condensed']" data-testid="billing-title">
                    Facturacion & Plan
                </h1>
                <p className="text-sm text-muted-foreground mt-1">Revisa tu plan, el cobro mensual y tu estado de cuenta.</p>
            </div>

            {/* Current Plan Card */}
            <Card className="card-aviation overflow-hidden" data-testid="current-plan-card">
                <div className="h-1 bg-primary" />
                <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                        <div className="flex items-center gap-4">
                            <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                                <Shield className="w-7 h-7 text-primary" />
                            </div>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h2 className="text-xl font-bold font-['Barlow_Condensed'] uppercase">{planNames[planKey] || planKey}</h2>
                                    <Badge className={planColors[planKey]} data-testid="plan-badge">{planKey.toUpperCase()}</Badge>
                                </div>
                                <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground">
                                    <span className="font-mono font-bold text-foreground text-lg">{usd(plan_details.price_per_aircraft)}<span className="text-sm text-muted-foreground font-normal"> por aeronave facturable/mes</span></span>
                                    <span className="text-xs">({tenant.billing_cycle === 'yearly' ? 'Anual' : 'Mensual'})</span>
                                </div>
                            </div>
                        </div>
                        <div className="flex items-center gap-3">
                            {tenant.status === 'trial' && (
                                <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20" data-testid="trial-badge">
                                    <Clock className="w-3 h-3 mr-1" /> Periodo de Prueba
                                </Badge>
                            )}
                            <Badge className={tenant.subscription_status === 'active' ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'} data-testid="subscription-status-badge">
                                {tenant.subscription_status === 'active' ? 'Activa' : 'Inactiva'}
                            </Badge>
                        </div>
                    </div>
                    {tenant.trial_ends_at && tenant.status === 'trial' && !data.billing?.pilot_active && new Date(tenant.trial_ends_at) > new Date() && (
                        <div className="mt-4 p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm flex items-center gap-2" data-testid="trial-notice">
                            <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                            <span className="text-muted-foreground">
                                Tu periodo de prueba termina el <strong className="text-foreground">{new Date(tenant.trial_ends_at).toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</strong>. Contacta al administrador de AeroOps para actualizar tu plan.
                            </span>
                        </div>
                    )}
                    <MonthlyBreakdown b={data.billing} />
                </CardContent>
            </Card>

            {!data.stripe_enabled && <AccountStatementCard />}

            {/* FASE 7: Subscription Card (oculta mientras STRIPE_ENABLED=false) */}
            {sub && data.stripe_enabled && (
                <Card className="card-aviation" data-testid="subscription-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                            <RefreshCw className="w-4 h-4 text-primary" /> Suscripcion Stripe
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                        <div className="grid sm:grid-cols-3 gap-4 text-sm">
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider">Estado</div>
                                <div className="font-bold mt-1">
                                    {sub.subscription_status === 'active' && <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20">Activa</Badge>}
                                    {sub.subscription_status === 'past_due' && <Badge className="bg-red-500/10 text-red-500 border-red-500/20">Pago vencido</Badge>}
                                    {sub.subscription_status === 'canceling' && <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20">Cancelando</Badge>}
                                    {sub.subscription_status === 'canceled' && <Badge className="bg-zinc-500/10 text-zinc-400 border-zinc-500/20">Cancelada</Badge>}
                                    {!['active','past_due','canceling','canceled'].includes(sub.subscription_status) && <Badge>{sub.subscription_status || 'N/A'}</Badge>}
                                </div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider">Ciclo</div>
                                <div className="font-mono font-bold mt-1 capitalize">{sub.billing_cycle || 'mensual'}</div>
                            </div>
                            <div>
                                <div className="text-xs text-muted-foreground uppercase tracking-wider">Ultimo pago</div>
                                <div className="font-mono font-bold mt-1">
                                    {sub.last_payment_at ? new Date(sub.last_payment_at).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' }) : '-'}
                                </div>
                            </div>
                        </div>
                        {sub.cancel_at_period_end && (
                            <div className="p-3 rounded-lg bg-amber-500/5 border border-amber-500/20 text-sm flex items-center gap-2" data-testid="cancel-notice">
                                <AlertCircle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                                <span>Tu suscripcion se cancelara al finalizar el periodo actual. Podes reactivarla en cualquier momento.</span>
                            </div>
                        )}
                        <div className="flex flex-wrap gap-2 pt-2">
                            <Button size="sm" onClick={() => setUpgradeOpen(true)} data-testid="upgrade-plan-btn">
                                <ArrowUpCircle className="w-4 h-4 mr-2" />
                                {sub.stripe_subscription_id ? 'Cambiar plan' : 'Activar suscripcion'}
                            </Button>
                            {sub.stripe_subscription_id && !sub.cancel_at_period_end && (
                                <Button size="sm" variant="outline" onClick={() => setConfirmCancel(true)} data-testid="cancel-sub-btn">
                                    <XSquare className="w-4 h-4 mr-2" /> Cancelar
                                </Button>
                            )}
                            {sub.cancel_at_period_end && (
                                <Button size="sm" variant="outline" onClick={() => setConfirmResume(true)} data-testid="resume-sub-btn">
                                    <RefreshCw className="w-4 h-4 mr-2" /> Reactivar
                                </Button>
                            )}
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Usage */}
            <Card className="card-aviation" data-testid="usage-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                        <Zap className="w-4 h-4 text-primary" /> Uso del Plan
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-5">
                    <UsageBar label="Aeronaves facturables" icon={Plane} current={usage.aircraft.current} max={usage.aircraft.max} />
                    <UsageBar label="Usuarios" icon={Users} current={usage.users.current} max={usage.users.max} />
                    <UsageBar label="Vuelos este mes" icon={BarChart3} current={usage.flights_monthly.current} max={usage.flights_monthly.max} />
                </CardContent>
            </Card>

            {/* Available Plans Comparison */}
            <Card className="card-aviation" data-testid="plans-comparison-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                        <ArrowUpRight className="w-4 h-4 text-primary" /> Planes Disponibles
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                        {Object.entries({
                            free: { name: 'Free Trial', price: '$0', unit: '' },
                            basic: { name: 'Starter', price: '$390', unit: '/aeronave al mes' },
                            pro: { name: 'Professional', price: '$320', unit: '/aeronave al mes' },
                            enterprise: { name: 'Enterprise', price: '$260', unit: '/aeronave al mes' },
                        }).map(([key, plan]) => {
                            const isCurrent = key === planKey;
                            const details = {
                                free: { ac: 'Hasta 2', us: 5, fl: 50 },
                                basic: { ac: '1 a 5', us: 15, fl: 200 },
                                pro: { ac: '6 a 15', us: 40, fl: 800 },
                                enterprise: { ac: '16 o más', us: 100, fl: 2500 },
                            }[key];
                            return (
                                <div key={key} className={`p-4 rounded-xl border ${isCurrent ? 'border-primary/50 bg-primary/5' : 'border-border/50'}`} data-testid={`plan-option-${key}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <span className="text-sm font-bold font-['Barlow_Condensed'] uppercase">{plan.name}</span>
                                        {isCurrent && <span className="text-[10px] font-bold text-primary uppercase">Actual</span>}
                                    </div>
                                    <div className="text-xl font-bold font-mono">{plan.price}<span className="text-xs text-muted-foreground font-normal">{plan.unit}</span></div>
                                    <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                                        <div>{details.ac} aeronaves</div>
                                        <div>{details.us} usuarios</div>
                                        <div>{details.fl} vuelos/mes</div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <p className="text-xs text-muted-foreground mt-4 text-center">
                        Los módulos adicionales tienen precio fijo mensual. Para cambiar de plan contacta a soporte de AeroOps.
                    </p>
                </CardContent>
            </Card>

            {/* FASE 7: Invoices de subscripcion (webhooks) */}
            {data.stripe_enabled && sub?.invoices?.length > 0 && (
                <Card className="card-aviation" data-testid="subscription-invoices-card">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                            <CreditCard className="w-4 h-4 text-primary" /> Facturas Recurrentes
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="sub-invoices-table">
                                <thead>
                                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/50">
                                        <th className="text-left py-3 px-3 font-bold">Fecha</th>
                                        <th className="text-left py-3 px-3 font-bold">Periodo</th>
                                        <th className="text-right py-3 px-3 font-bold">Monto</th>
                                        <th className="text-left py-3 px-3 font-bold">Estado</th>
                                        <th className="text-left py-3 px-3 font-bold">Recibo</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {sub.invoices.map((inv, i) => {
                                        const isPaid = inv.status === 'paid';
                                        return (
                                            <tr key={inv.id} className="border-b border-border/30 hover:bg-muted/30" data-testid={`sub-invoice-row-${i}`}>
                                                <td className="py-3 px-3 font-mono text-xs">{new Date(inv.created_at).toLocaleDateString('es-ES', { day:'2-digit', month:'short', year:'numeric' })}</td>
                                                <td className="py-3 px-3 text-xs text-muted-foreground">
                                                    {inv.period_start && inv.period_end
                                                        ? `${new Date(inv.period_start*1000).toLocaleDateString('es-ES', {day:'2-digit',month:'short'})} → ${new Date(inv.period_end*1000).toLocaleDateString('es-ES', {day:'2-digit',month:'short'})}`
                                                        : '-'}
                                                </td>
                                                <td className="py-3 px-3 text-right font-mono font-bold">${(inv.amount_paid ?? inv.amount_due ?? 0).toFixed(2)} {(inv.currency || 'usd').toUpperCase()}</td>
                                                <td className="py-3 px-3">
                                                    <Badge className={isPaid ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/20' : 'bg-red-500/10 text-red-500 border-red-500/20'}>
                                                        {isPaid ? 'Pagada' : 'Fallida'}
                                                    </Badge>
                                                </td>
                                                <td className="py-3 px-3">
                                                    {inv.hosted_invoice_url ? (
                                                        <a href={inv.hosted_invoice_url} target="_blank" rel="noreferrer" className="text-primary hover:underline inline-flex items-center gap-1 text-xs">
                                                            Ver <ExternalLink className="w-3 h-3" />
                                                        </a>
                                                    ) : <span className="text-xs text-muted-foreground">-</span>}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </CardContent>
                </Card>
            )}

            {/* Transaction History */}
            <Card className="card-aviation" data-testid="transactions-card">
                <CardHeader className="pb-3">
                    <CardTitle className="text-base font-['Barlow_Condensed'] uppercase tracking-wide flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-primary" /> Historial de Pagos
                    </CardTitle>
                </CardHeader>
                <CardContent>
                    {transactions.length === 0 ? (
                        <div className="text-center py-10 text-muted-foreground" data-testid="no-transactions">
                            <CreditCard className="w-10 h-10 mx-auto mb-3 opacity-20" />
                            <p className="text-sm">No hay transacciones registradas.</p>
                            <p className="text-xs mt-1">Los pagos apareceran aqui una vez se procesen.</p>
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="transactions-table">
                                <thead>
                                    <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b border-border/50">
                                        <th className="text-left py-3 px-3 font-bold">Fecha</th>
                                        <th className="text-left py-3 px-3 font-bold">Plan</th>
                                        <th className="text-left py-3 px-3 font-bold">Ciclo</th>
                                        <th className="text-right py-3 px-3 font-bold">Monto</th>
                                        <th className="text-left py-3 px-3 font-bold">Estado</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {transactions.map((txn, i) => {
                                        const st = statusConfig[txn.status] || statusConfig.pending;
                                        const StIcon = st.icon;
                                        return (
                                            <tr key={txn.id || i} className="border-b border-border/30 hover:bg-muted/30 transition-colors" data-testid={`transaction-row-${i}`}>
                                                <td className="py-3 px-3 font-mono text-xs">
                                                    {txn.created_at ? new Date(txn.created_at).toLocaleDateString('es-ES', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                                                </td>
                                                <td className="py-3 px-3">
                                                    <Badge className={planColors[txn.plan] || planColors.free}>{planNames[txn.plan] || txn.plan}</Badge>
                                                </td>
                                                <td className="py-3 px-3 text-xs text-muted-foreground capitalize">{txn.billing_cycle || '-'}</td>
                                                <td className="py-3 px-3 text-right font-mono font-bold">${txn.amount ? (txn.amount / 100).toFixed(2) : '0.00'}</td>
                                                <td className="py-3 px-3">
                                                    <div className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${st.bg} ${st.color}`}>
                                                        <StIcon className="w-3 h-3" />{st.label}
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* FASE 7: Dialogs (cancel / resume / upgrade) */}
            <Dialog open={confirmCancel} onOpenChange={setConfirmCancel}>
                <DialogContent data-testid="cancel-dialog">
                    <DialogHeader>
                        <DialogTitle>Cancelar suscripcion</DialogTitle>
                        <DialogDescription>
                            La cancelacion sera efectiva al final del periodo actual. Podes reactivarla en cualquier momento antes de esa fecha.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmCancel(false)}>Volver</Button>
                        <Button variant="destructive" onClick={doCancel} disabled={actionLoading} data-testid="confirm-cancel-btn">
                            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Cancelar suscripcion
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={confirmResume} onOpenChange={setConfirmResume}>
                <DialogContent data-testid="resume-dialog">
                    <DialogHeader>
                        <DialogTitle>Reactivar suscripcion</DialogTitle>
                        <DialogDescription>
                            Se cancelara la solicitud de cancelacion programada. Tu suscripcion continuara renovandose normalmente.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setConfirmResume(false)}>Volver</Button>
                        <Button onClick={doResume} disabled={actionLoading} data-testid="confirm-resume-btn">
                            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Reactivar
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <Dialog open={upgradeOpen} onOpenChange={setUpgradeOpen}>
                <DialogContent data-testid="upgrade-dialog">
                    <DialogHeader>
                        <DialogTitle>Actualizar plan</DialogTitle>
                        <DialogDescription>
                            Seras redirigido a Stripe Checkout para completar el pago. El nuevo plan se activa automaticamente tras el cobro exitoso.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-2">
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Plan</label>
                            <Select value={upgradePlan} onValueChange={setUpgradePlan}>
                                <SelectTrigger data-testid="upgrade-plan-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="basic">Starter — $390/aeronave</SelectItem>
                                    <SelectItem value="pro">Professional — $320/aeronave</SelectItem>
                                    <SelectItem value="enterprise">Enterprise — $260/aeronave</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <div>
                            <label className="text-xs uppercase tracking-wider text-muted-foreground">Ciclo de facturacion</label>
                            <Select value={upgradeCycle} onValueChange={setUpgradeCycle}>
                                <SelectTrigger data-testid="upgrade-cycle-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="monthly">Mensual</SelectItem>
                                    <SelectItem value="yearly">Anual (~2 meses gratis)</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setUpgradeOpen(false)}>Cancelar</Button>
                        <Button onClick={doUpgrade} disabled={actionLoading} data-testid="confirm-upgrade-btn">
                            {actionLoading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                            Ir a Stripe Checkout
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
